import React from 'react';
import type { PosterProps } from './posterTypes';

/**
 * UK council at-stop timetable poster (template T1, milestone M1).
 *
 * A4 portrait, faithful to the Cumberland reference posters: dark header band with
 * locality/stop name + valid-from, a "This Stop" strip, one column per service with
 * a day-type subheader and the full explicit time list, and a footer with a note
 * legend, info line, QR placeholder and the ATCO/NaPTAN stop code.
 *
 * Plain divs + inline styles (not MUI sx) so the printed output is self-contained
 * and predictable. Branding (logo/help/T&C/QR link) is placeholder for M1 — wired
 * from operator feature-flag config in M2/M5.
 */
const NAVY = '#0f2d3d';
const BLUE = '#1f4e9b';

export function UkCouncilPoster({
  data,
  locality,
  validFrom,
  brandColor = NAVY,
  infoLine = 'For timetable information visit your local transport authority website.',
}: PosterProps) {
  return (
    // A4 portrait at 96dpi ≈ 794×1123px; width fixed, height grows with content.
    <div
      className="uk-poster"
      style={{
        width: 794,
        minHeight: 1123,
        boxSizing: 'border-box',
        background: '#fff',
        color: '#111',
        fontFamily: 'Arial, Helvetica, sans-serif',
        display: 'flex',
        flexDirection: 'column',
        border: '1px solid #ddd',
      }}
    >
      {/* Header band */}
      <div style={{ background: brandColor, color: '#fff', padding: '16px 20px' }}>
        {locality ? <div style={{ fontSize: 22, lineHeight: 1.1 }}>{locality}</div> : null}
        <div style={{ fontSize: 34, fontWeight: 700, lineHeight: 1.1 }}>{data.stopName}</div>
        <div style={{ fontSize: 13, marginTop: 4, opacity: 0.9 }}>Valid from {validFrom}</div>
      </div>

      {/* This Stop strip */}
      <div
        style={{
          background: BLUE,
          color: '#ffe14d',
          fontWeight: 700,
          fontSize: 20,
          textAlign: 'center',
          padding: '8px 0',
          letterSpacing: 0.5,
        }}
      >
        This Stop
      </div>

      {/* Columns: one per service calling here */}
      <div style={{ flex: 1, display: 'flex', gap: 12, padding: 16, alignItems: 'flex-start' }}>
        {data.columns.length === 0 ? (
          <div style={{ padding: 24, color: '#666', fontSize: 15 }}>
            No scheduled departures found for this stop.
          </div>
        ) : (
          data.columns.map((col) => (
            <div key={col.routeId} style={{ flex: 1, minWidth: 0, border: '1px solid #e0e0e0' }}>
              {/* Column header: destination + service number + operator */}
              <div style={{ background: brandColor, color: '#fff', padding: '8px 10px' }}>
                <div style={{ fontSize: 12, opacity: 0.85 }}>to</div>
                <div style={{ fontSize: 22, fontWeight: 700, lineHeight: 1.1 }}>
                  {col.destinationLabel.replace(/^to\s+/i, '')}
                </div>
                <div style={{ fontSize: 14, marginTop: 4 }}>
                  {col.serviceNumber ? <strong>{col.serviceNumber}</strong> : null}
                  {col.operator ? <span style={{ opacity: 0.85 }}>{'  '}{col.operator}</span> : null}
                </div>
              </div>

              {col.dayGroups.map((group, gi) => (
                <div key={gi}>
                  <div
                    style={{
                      background: brandColor,
                      color: '#fff',
                      fontWeight: 700,
                      fontSize: 13,
                      textAlign: 'center',
                      padding: '4px 0',
                      borderTop: '2px solid #fff',
                    }}
                  >
                    {group.label}
                  </div>
                  <div>
                    {group.times.map((time, ti) => (
                      <div
                        key={`${time}-${ti}`}
                        style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          padding: '2px 10px',
                          fontSize: 14,
                          borderBottom: '1px solid #eee',
                        }}
                      >
                        <span>{time}</span>
                        {col.serviceNumber ? (
                          <span style={{ color: '#555' }}>{col.serviceNumber}</span>
                        ) : null}
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          ))
        )}
      </div>

      {/* Footer: info line, QR placeholder, stop code */}
      <div style={{ borderTop: `3px solid ${brandColor}`, padding: '12px 16px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
          <div
            aria-hidden
            title="QR to live departures (placeholder)"
            style={{
              width: 72,
              height: 72,
              flexShrink: 0,
              border: '2px dashed #999',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: 10,
              color: '#999',
              textAlign: 'center',
            }}
          >
            QR
          </div>
          <div style={{ flex: 1, fontSize: 12, color: '#333' }}>
            <div style={{ fontWeight: 700, marginBottom: 2 }}>Information</div>
            <div>{infoLine}</div>
            <div style={{ marginTop: 6, color: '#888' }}>
              Logo / help text / T&amp;C link — placeholder (operator branding, M2).
            </div>
          </div>
          <div style={{ textAlign: 'right', fontSize: 11, color: '#666' }}>
            {data.atcoCode ? <div>ATCO {data.atcoCode}</div> : null}
            {data.naptanCode ? <div>NaPTAN {data.naptanCode}</div> : null}
          </div>
        </div>
      </div>
    </div>
  );
}

export default UkCouncilPoster;
